import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ProdGraphPanel from 'productionUnit/components/ProdGraphPanel'
import { Grid } from '@mui/material'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { useParams } from 'react-router'
import { componentHasHabilitations } from 'utils/HabilitationUtil'
import { H_MAT_MODULE, H_UNITS_DASHBOARD } from 'account/constants/AccessRulesConstants'
import { getLinks, getStationTitleWithTownName } from 'utils/StationUtils'
import { getStationArrowNav } from 'utils/ActionUtils'
import UnitChartPanel from './UnitChartPanel'
import StationMapDashboardPanel from 'station/components/dashboard/component/map/StationMapDashboardPanel'
import StationDescription from 'station/components/description/StationDescription'
import HomeAction from 'home/actions/HomeAction'
import UnitAction from 'referencial/components/unit/actions/UnitAction'
import ProductionPiezometerAlertPanel from './alerts/ProductionPiezometerAlertPanel'
import HabilitationRequired from 'utils/components/HabilitationRequired'
import MaterielAssigned from 'station/components/materiel/MaterielAssigned'
import LastEventPanel from 'station/components/event/LastEventPanel'
import SmallPicturePanel from 'components/picture/SmallPicturePanel'
import FilePanel from 'components/file/FilePanel'
import HydrogeologicalEntityAction from 'referencial/components/hydrogeologicalEntity/actions/HydrogeologicalEntityAction'
import CampaignAction from 'campaign/actions/CampaignAction'
import QualityAction from 'quality/actions/QualityAction'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import ProductionUnitLinkedPrel from 'productionUnit/components/ProductionUnitLinkedPrel'
import { ProductionThresholdIndicators } from 'piezometry/components/alerts/thresholds/ThresholdIndicatorsManager'
import WaterStressCard from '../../station/components/indicator/WaterStressCard'
import WaterStressChartPanel from '../../station/components/indicator/WaterStressChartPanel'
import { useWaterStressEnabled } from '../../station/components/indicator/ObservatoryFeaturesUtils'
import { INDICATOR_OBJECT_TYPE } from '../../import/constants/IndicatorsConstants'

const ProductionUnitDashboard = ({
}) => {
    const dispatch = useDispatch()
    const { id } = useParams()

    const {
        productionUnit,
        pictures,
        files,
        units,
        productionUnitEvents,
        productionUnits,
        measures,
        stationCentral,
        stationPowerSupply,
        stationSensor,
        stationSim,
        stationTelecom,
        stationVariousMateriel,
        stationEquipment,
        stationSubscriptionAssignments,
        citiesIndex,
    } = useSelector(store => ({
        productionUnit: store.ProductionUnitReducer.productionUnit,
        pictures: store.StationReducer.pictures,
        files: store.StationReducer.files,
        units: store.UnitReducer.units,
        productionUnitEvents: store.ProductionUnitReducer.productionUnitEvents,
        productionUnits: store.ProductionUnitReducer.productionUnits,
        measures: store.PiezometerStationReducer.measures,
        stationCentral: store.StationReducer.stationCentral,
        stationPowerSupply: store.StationReducer.stationPowerSupply,
        stationSensor: store.StationReducer.stationSensor,
        stationSim: store.StationReducer.stationSim,
        stationTelecom: store.StationReducer.stationTelecom,
        stationVariousMateriel: store.StationReducer.stationVariousMateriel,
        stationEquipment: store.StationReducer.stationEquipment,
        stationSubscriptionAssignments: store.StationReducer.stationSubscriptionAssignments,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const waterStressEnabled = useWaterStressEnabled()

    useEffect(() => {
        const isAuthorized = componentHasHabilitations(H_UNITS_DASHBOARD)
        if (!isAuthorized) { // A modifier quand react-router sera à jour
            push('/unauthorized')
            return
        }
        dispatch(HomeAction.setBackPath('/productionUnit'))
        dispatch(UnitAction.fetchUnits())
        dispatch(QualityAction.fetchThresholds())
        dispatch(HydrogeologicalEntityAction.fetchHydrogeologicalEntities())
        dispatch(CampaignAction.fetchPiezometryCampaignsProgress())
    }, [])

    useActions(() => {
        return {
            links: getLinks(productionUnit, {}),
            arrowNav: getStationArrowNav(STATION_TYPE_NAME.productionUnit, productionUnits, productionUnit.id, s => dispatch(push(`/station/productionUnit/${s.id}`))),
        }
    }, [productionUnit, units, productionUnits])

    const townName = useMemo(() => citiesIndex[productionUnit.townCode]?.name, [citiesIndex, productionUnit])

    useTitle(() => [{
        title: i18n.productionUnit,
        href: STATION_TYPE_NAME.productionUnit,
    }, {
        title: getStationTitleWithTownName(productionUnit, townName),
        href: `station/productionUnit/${productionUnit.id}`,
    }], [productionUnit, townName])

    const onShowMore = () => {
        dispatch(push(`/station/productionUnit/${productionUnit.id}/picture`))
    }

    return (
        <Grid
            container
            spacing={1}
            sx={{
                paddingTop: '10px',
                paddingBottom: '150px',
                paddingLeft: '20px',
                paddingRight: '10px',
            }}
        >
            <Grid container size={9}>
                <Grid size={12}>
                    <StationDescription id={id} stationTypes={[STATION_TYPE_NAME.productionUnit]} noOwner />
                </Grid>
                <Grid size={12}>
                    <ProdGraphPanel />
                </Grid>
                {waterStressEnabled && (
                    <Grid size={12}>
                        <WaterStressChartPanel
                            objectId={productionUnit.id}
                            objectType={INDICATOR_OBJECT_TYPE.PRODUCTION_UNIT}
                        />
                    </Grid>
                )}
                <UnitChartPanel id={productionUnit.id} productionUnit={productionUnit} fromStationType='PROD' />
                <Grid size={12}>
                    <ProductionUnitLinkedPrel />
                </Grid>
            </Grid>
            <Grid container direction='column' size={3} spacing={1}>
                { !!measures.legnth && (
                    <ProductionPiezometerAlertPanel />
                ) }
                { !!(stationCentral.length || stationPowerSupply.length || stationSensor.length
                    || stationSim.length || stationTelecom.length || stationVariousMateriel.length
                    || stationEquipment.length || stationSubscriptionAssignments.length)
                    && (
                        <HabilitationRequired habilitation={H_MAT_MODULE}>
                            <MaterielAssigned stationId={id} type={STATION_TYPE_NAME.productionUnit} />
                        </HabilitationRequired>
                    )
                }
                { !!productionUnitEvents.length && (
                    <LastEventPanel
                        id={id}
                        events={productionUnitEvents}
                        stationId={id}
                        stationType={STATION_TYPE_NAME.productionUnit}
                    />
                )}
                {waterStressEnabled && (
                    <WaterStressCard
                        objectType={INDICATOR_OBJECT_TYPE.PRODUCTION_UNIT}
                        objectCode={productionUnit.code}
                    />
                )}
                <ProductionThresholdIndicators />
                <StationMapDashboardPanel
                    noMarkerTooltip
                    station={productionUnit}
                    type={STATION_TYPE_NAME.productionUnit}
                />
                {!!pictures?.length && (
                    <SmallPicturePanel pictures={pictures} />
                )}
                {!!files?.length && (
                    <FilePanel code={productionUnit.code} files={files} hideTitle onShowMore={onShowMore} />
                )}
            </Grid>
        </Grid>
    )
}

ProductionUnitDashboard.propTypes = {
}

export default ProductionUnitDashboard
